import { useTranslation } from "react-i18next";
import {
  ButtonWithWarningDialog,
  LinkButtonLargeV2,
} from "../../../../components/Buttons/Buttons";
import {
  FullWidthHorizontalSeparator,
  PageTitle,
  PageWrapper,
} from "../../../../layout/portalPageLayout";
import { useEffect, useState } from "react";
import { BooleanParam, StringParam, useQueryParams } from "use-query-params";
import { useHistory } from "react-router-dom";
import { useRoutePath } from "../../../../util/Routing";
import { UploadPriceTiers } from "./UploadPriceTiers";
import { DownloadPriceTiers } from "./DownloadPriceTiers";
import axios from "axios";
import { useStoreState } from "../../../../util/util";
import { DelayedSpinner } from "../../../../components/DelayedSpinner/DelayedSpinner";
import { useNotifications } from "../../../../components/Notifications/NotificationsContext";
import { CaretLeft } from "../../../../components/Icons/Icons";

/**
 * BulkUploadPriceTiers component
 * This component will handle the bulk upload of price tiers for customers.
 *
 * @returns JSX.Element
 */
export function BulkUploadPriceTiers(): JSX.Element {
  const { storefront_id } = useStoreState();
  const { t } = useTranslation();
  const { adminPath } = useRoutePath();
  const { notifyError } = useNotifications();
  const history = useHistory();
  const [canExit, setCanExit] = useState<boolean>(true);
  const [upload_id, setUploadID] = useState<string>();
  const [price_tiers_csv_id, setPriceTiersCsvID] = useState<string>();
  const [priceTiersError, setPriceTiersError] = useState<boolean>(false);
  const [stage, setStage] = useState<"download" | "upload">("download");
  const [query, setQuery] = useQueryParams({
    uploadUUID: StringParam,
    shouldMutate: BooleanParam,
  });

  const onCompleteUpload = () => {
    setStage("download");
    setQuery({
      uploadUUID: undefined,
      shouldMutate: true,
    });
    setUploadID(undefined);
    setCanExit(true);
  };

  const goBack = () => {
    if (stage === "download") {
      history.push(`${adminPath}/tenants`);
    } else {
      onCompleteUpload();
    }
  };

  useEffect(() => {
    if (query.uploadUUID && !upload_id) {
      setUploadID(query.uploadUUID);
      setStage("upload");
      setCanExit(false);
    }
  }, [query.uploadUUID, upload_id]);

  useEffect(() => {
    const getPriceTiersCsvID = async () => {
      try {
        const params = new URLSearchParams();
        params.append("include_data", "true");
        const response = await axios.get<{
          job_id: string;
          status: string;
          message: string;
        }>(
          `/v2/storefronts/${storefront_id}/price-tiers/bulk-upload/template?${params}`
        );
        const { job_id } = response.data;
        setPriceTiersCsvID(job_id);
      } catch (error) {
        notifyError(
          t(
            "An error occurred while fetching price tiers csv id. Please refresh the page."
          )
        );
        setPriceTiersError(true);
      }
    };
    if (!price_tiers_csv_id && !priceTiersError) {
      getPriceTiersCsvID();
    }
  }, [notifyError, priceTiersError, price_tiers_csv_id, storefront_id, t]);

  return (
    <PageWrapper id="bulkupload_pricetiers_container">
      <div style={{ width: "fit-content" }}>
        {canExit ? (
          <LinkButtonLargeV2 onClick={goBack}>
            <CaretLeft />
            {t("Back")}
          </LinkButtonLargeV2>
        ) : (
          <ButtonWithWarningDialog
            Button={(props) => (
              <LinkButtonLargeV2 {...props}>
                <CaretLeft />
                {t("Back")}
              </LinkButtonLargeV2>
            )}
            testid={`close_upload_${upload_id}`}
            confirmMessage={t(
              "Your price tiers upload is in progress. Are you sure you want to leave this page?"
            )}
            handleConfirm={goBack}
            heading={t("Go Back?")}
            id={upload_id}
          />
        )}
      </div>
      <PageTitle>{t("Bulk Upload Price Tiers")}</PageTitle>
      <FullWidthHorizontalSeparator />
      {stage === "download" ? (
        <>
          {price_tiers_csv_id && !priceTiersError ? (
            <DownloadPriceTiers
              goBack={goBack}
              setStage={setStage}
              price_tiers_csv_id={price_tiers_csv_id}
            />
          ) : (
            <DelayedSpinner />
          )}
        </>
      ) : (
        <UploadPriceTiers goBack={goBack} onCompleteUpload={onCompleteUpload} />
      )}
    </PageWrapper>
  );
}
